import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Package, ShoppingCart, FileText, Star, ShieldCheck, Users, Plus, Percent, Box, Tag, BarChart3, Download, Pencil, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import SalesDashboard from '../components/SalesDashboard';
import { FullPageLoader } from '../components/MedicalLoader';
import ImageUpload from '../components/ImageUpload';

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bulkEnquiries, setBulkEnquiries] = useState([]);
  const [b2bEnquiries, setB2bEnquiries] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // Track which product is being edited
  
  // Available payment methods
  const PAYMENT_METHODS = [
    { id: 'razorpay', name: 'Card Payment', description: 'Debit/Credit Cards, NetBanking' },
    { id: 'upi', name: 'UPI Payment', description: 'GPay, PhonePe, Paytm, BHIM' },
    { id: 'cod', name: 'Cash on Delivery', description: 'Pay when you receive' },
    { id: 'bank_transfer', name: 'Bank Transfer', description: 'NEFT/RTGS/IMPS' },
    { id: 'pay_later', name: 'Pay Later', description: 'Pay within 30 days' },
  ];

  // Default empty form state
  const defaultProductForm = {
    name: '',
    description: '',
    category: '',
    price: '',
    originalPrice: '',
    discount: '',
    priceError: '',
    image: '',
    images: '',
    stockQuantity: '',
    minOrderQuantity: '1',
    specifications: {},
    keyFeatures: '',
    featureHighlights: [
      { icon: 'zap', title: '', description: '' },
      { icon: 'shield', title: '', description: '' },
      { icon: 'timer', title: '', description: '' },
      { icon: 'award', title: '', description: '' },
    ],
    warrantyInfo: '',
    shippingInfo: '',
    paymentMethods: ['razorpay', 'upi', 'cod', 'bank_transfer', 'pay_later'], // All enabled by default
    availability: true,
    featured: false,
    limitedStock: false,
  };
  
  // Enhanced Product form with more fields
  const [productForm, setProductForm] = useState(defaultProductForm);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes, enquiriesRes, b2bEnquiriesRes, partnerAppsRes, reviewsRes, verificationsRes] = await Promise.all([
        api.get('/products'),
        api.get('/admin/orders'),
        api.get('/admin/bulk-enquiries'),
        api.get('/admin/b2b-enquiries'),
        api.get('/admin/partner-applications'),
        api.get('/admin/reviews'),
        api.get('/admin/verifications'),
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setBulkEnquiries(enquiriesRes.data);
      setB2bEnquiries(b2bEnquiriesRes.data);
      setPartnerApplications(partnerAppsRes.data);
      setReviews(reviewsRes.data);
      setVerifications(verificationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    // Validate pricing
    const sellingPrice = parseFloat(productForm.price) || 0;
    const originalPrice = parseFloat(productForm.originalPrice) || 0;
    
    if (originalPrice > 0 && sellingPrice >= originalPrice) {
      toast.error('Selling price must be less than Original Price (MRP)');
      return;
    }
    
    if (sellingPrice <= 0) {
      toast.error('Please enter a valid selling price');
      return;
    }
    
    try {
      // Parse images from comma-separated string
      const imagesArray = productForm.images 
        ? productForm.images.split(',').map(url => url.trim()).filter(url => url)
        : [];
      
      // Parse key features from comma-separated string
      const keyFeaturesArray = productForm.keyFeatures 
        ? productForm.keyFeatures.split(',').map(f => f.trim()).filter(f => f)
        : ['Premium Quality', 'ISO Certified', 'Easy to Use', '1 Year Warranty'];
      
      // Filter out empty feature highlights
      const featureHighlightsArray = productForm.featureHighlights
        .filter(fh => fh.title && fh.description)
        .map(fh => ({ icon: fh.icon, title: fh.title, description: fh.description }));

      const productData = {
        name: productForm.name,
        description: productForm.description,
        category: productForm.category,
        price: parseFloat(productForm.price),
        original_price: productForm.originalPrice ? parseFloat(productForm.originalPrice) : parseFloat(productForm.price),
        image: productForm.image,
        images: imagesArray.length > 0 ? imagesArray : null,
        key_features: keyFeaturesArray,
        feature_highlights: featureHighlightsArray.length > 0 ? featureHighlightsArray : null,
        warranty_info: productForm.warrantyInfo || null,
        shipping_info: productForm.shippingInfo || null,
        payment_methods: productForm.paymentMethods.length > 0 ? productForm.paymentMethods : null,
        availability: productForm.availability,
        specifications: {
          ...productForm.specifications,
          ...(productForm.stockQuantity && { stockQuantity: parseInt(productForm.stockQuantity) }),
          ...(productForm.minOrderQuantity && { minOrderQuantity: parseInt(productForm.minOrderQuantity) }),
          featured: productForm.featured,
          limitedStock: productForm.limitedStock,
        },
      };
      
      if (editingProduct) {
        // Update existing product
        await api.put(`/admin/products/${editingProduct.id}`, productData);
        toast.success('Product updated successfully!');
      } else {
        // Create new product
        await api.post('/admin/products', productData);
        toast.success('Product created successfully!');
      }
      
      setProductDialogOpen(false);
      setEditingProduct(null);
      fetchData();
      setProductForm(defaultProductForm);
    } catch (error) {
      toast.error(editingProduct ? 'Failed to update product' : 'Failed to create product');
    }
  };

  // Open edit dialog with product data
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    
    // Parse feature highlights from product
    const featureHighlights = product.feature_highlights && product.feature_highlights.length > 0
      ? [
          ...product.feature_highlights,
          ...Array(4 - product.feature_highlights.length).fill({ icon: 'zap', title: '', description: '' })
        ].slice(0, 4)
      : [
          { icon: 'zap', title: '', description: '' },
          { icon: 'shield', title: '', description: '' },
          { icon: 'timer', title: '', description: '' },
          { icon: 'award', title: '', description: '' },
        ];
    
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      category: product.category || '',
      price: product.price?.toString() || '',
      originalPrice: product.original_price?.toString() || '',
      discount: '',
      image: product.image || '',
      images: product.images?.join(', ') || '',
      stockQuantity: product.specifications?.stockQuantity?.toString() || '',
      minOrderQuantity: product.specifications?.minOrderQuantity?.toString() || '1',
      specifications: product.specifications || {},
      keyFeatures: product.key_features?.join(', ') || '',
      featureHighlights: featureHighlights,
      warrantyInfo: product.warranty_info || '',
      shippingInfo: product.shipping_info || '',
      paymentMethods: product.payment_methods || ['razorpay', 'cod', 'bank_transfer', 'emi', 'pay_later'],
      availability: product.availability ?? true,
      featured: product.specifications?.featured ?? false,
      limitedStock: product.specifications?.limitedStock ?? false,
    });
    
    setProductDialogOpen(true);
  };

  // Toggle payment method selection
  const togglePaymentMethod = (methodId) => {
    setProductForm(prev => {
      const methods = prev.paymentMethods || [];
      if (methods.includes(methodId)) {
        return { ...prev, paymentMethods: methods.filter(m => m !== methodId) };
      } else {
        return { ...prev, paymentMethods: [...methods, methodId] };
      }
    });
  };

  // Close dialog and reset form
  const handleCloseDialog = () => {
    setProductDialogOpen(false);
    setEditingProduct(null);
    setProductForm(defaultProductForm);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleApproveReview = async (reviewId, approved) => {
    try {
      await api.put(`/admin/reviews/${reviewId}`, { approved });
      toast.success(approved ? 'Review approved' : 'Review rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleVerificationAction = async (verificationId, status) => {
    try {
      await api.put(`/admin/verifications/${verificationId}`, { status });
      toast.success(`Verification ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleEnquiryStatus = async (enquiryId, status) => {
    try {
      await api.put(`/admin/bulk-enquiries/${enquiryId}`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleB2bEnquiryStatus = async (enquiryId, status) => {
    try {
      await api.put(`/admin/b2b-enquiries/${enquiryId}`, { status });
      toast.success('B2B Enquiry status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update B2B enquiry status');
    }
  };

  const handlePartnerApplicationStatus = async (applicationId, status) => {
    try {
      await api.put(`/admin/partner-applications/${applicationId}`, { status });
      toast.success('Partner application status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update partner application status');
    }
  };

  const handleOrderStatusUpdate = async (orderId, status, courierName = null, trackingNumber = null, estimatedDelivery = null) => {
    try {
      const updateData = { order_status: status };
      if (courierName) updateData.courier_name = courierName;
      if (trackingNumber) updateData.tracking_number = trackingNumber;
      if (estimatedDelivery) updateData.estimated_delivery = estimatedDelivery;
      
      await api.put(`/admin/orders/${orderId}/status`, updateData);
      toast.success(`Order status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handlePaymentConfirmation = async (orderId, paymentStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/payment`, { payment_status: paymentStatus });
      toast.success(`Payment ${paymentStatus === 'completed' ? 'confirmed' : 'marked as ' + paymentStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  if (loading) {
    return <FullPageLoader text="Loading admin panel..." />;
  }

  return (
    <div className="min-h-screen bg-purple-50 py-4 sm:py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8" data-testid="admin-title">
          Admin <span className="text-purple-600">Dashboard</span>
        </h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-products">
            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{products.length}</p>
            <p className="text-xs text-gray-500">Products</p>
          </div>
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-orders">
            <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{orders.length}</p>
            <p className="text-xs text-gray-500">Orders</p>
          </div>
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-enquiries">
            <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{bulkEnquiries.length}</p>
            <p className="text-xs text-gray-500">Enquiries</p>
          </div>
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-b2b">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{b2bEnquiries.length}</p>
            <p className="text-xs text-gray-500">B2B Leads</p>
          </div>
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-partners">
            <Box className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{partnerApplications.length}</p>
            <p className="text-xs text-gray-500">Partners</p>
          </div>
          <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-4 shadow-sm" data-testid="stat-verifications">
            <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-gray-900">{verifications.filter(v => v.status === 'pending').length}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-auto min-w-full sm:grid sm:w-full sm:grid-cols-8 bg-white border border-purple-100 rounded-xl p-1 shadow-sm gap-1">
              <TabsTrigger value="dashboard" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-dashboard-tab">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Dashboard</span>
                <span className="sm:hidden">Stats</span>
              </TabsTrigger>
              <TabsTrigger value="products" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-products-tab">Products</TabsTrigger>
              <TabsTrigger value="orders" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-orders-tab">Orders</TabsTrigger>
              <TabsTrigger value="enquiries" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-enquiries-tab">Enquiries</TabsTrigger>
              <TabsTrigger value="b2b" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-b2b-tab">B2B</TabsTrigger>
              <TabsTrigger value="partners" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-partners-tab">Partners</TabsTrigger>
              <TabsTrigger value="reviews" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-reviews-tab">Reviews</TabsTrigger>
              <TabsTrigger value="verifications" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm data-[state=active]:bg-purple-600 data-[state=active]:text-white whitespace-nowrap" data-testid="admin-verifications-tab">Verify</TabsTrigger>
            </TabsList>
          </div>

          {/* Sales Dashboard Tab */}
          <TabsContent value="dashboard">
            <SalesDashboard />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Products Management</h2>
                <Dialog open={productDialogOpen} onOpenChange={(open) => {
                  if (!open) handleCloseDialog();
                  else setProductDialogOpen(true);
                }}>
                  <DialogTrigger asChild>
                    <Button className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-sm" data-testid="add-product-button" onClick={() => { setEditingProduct(null); setProductForm(defaultProductForm); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-purple-100 shadow-xl">
                    <DialogHeader className="border-b border-gray-100 pb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                          {editingProduct ? <Pencil className="w-5 h-5 text-purple-600" /> : <Package className="w-5 h-5 text-purple-600" />}
                        </div>
                        <div>
                          <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">
                            {editingProduct ? 'Edit Product' : 'Add New Product'}
                          </DialogTitle>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {editingProduct ? 'Update the product details below' : 'Fill in the product details below'}
                          </p>
                        </div>
                      </div>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4 sm:space-y-6 pt-4">
                      {/* Basic Info Section */}
                      <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <Tag className="w-4 h-4 mr-2 text-purple-500" />
                          Basic Information
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Product Name *</Label>
                            <Input
                              value={productForm.name}
                              onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                              required
                              placeholder="Enter product name"
                              className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                              data-testid="product-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Category *</Label>
                            <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })} required>
                              <SelectTrigger className="mt-1 border-gray-200 focus:border-purple-500 text-sm" data-testid="product-category-select">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Diagnostic Equipment">Diagnostic Equipment</SelectItem>
                                <SelectItem value="Hospital Furniture">Hospital Furniture</SelectItem>
                                <SelectItem value="Surgical Instruments">Surgical Instruments</SelectItem>
                                <SelectItem value="Patient Monitoring">Patient Monitoring</SelectItem>
                                <SelectItem value="Lab Equipment">Lab Equipment</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-700 font-medium text-sm">Description *</Label>
                          <Textarea
                            value={productForm.description}
                            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                            required
                            rows={3}
                            placeholder="Enter detailed product description"
                            className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                            data-testid="product-description-input"
                          />
                        </div>
                        
                        {/* Main Product Image Upload */}
                        <div className="space-y-2">
                          <Label className="text-gray-700 font-medium text-sm flex items-center">
                            <Upload className="w-4 h-4 mr-2 text-purple-500" />
                            Main Product Image *
                          </Label>
                          {productForm.image ? (
                            <div className="flex items-start gap-3">
                              <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                                <img src={productForm.image} alt="Product" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => setProductForm({ ...productForm, image: '' })}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <span className="text-xs">✕</span>
                                </button>
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-green-600 font-medium">✓ Image uploaded</p>
                                <p className="text-xs text-gray-400 mt-1 break-all">{productForm.image.substring(0, 50)}...</p>
                              </div>
                            </div>
                          ) : (
                            <ImageUpload
                              onUploadComplete={(url) => setProductForm({ ...productForm, image: url })}
                              multiple={false}
                              folder="alaxico/products"
                              existingImages={[]}
                              maxFiles={1}
                            />
                          )}
                          {/* Fallback: Manual URL input */}
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Or paste image URL directly:</p>
                            <Input
                              value={productForm.image}
                              onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                              placeholder="https://example.com/image.jpg"
                              className="border-gray-200 focus:border-purple-500 text-sm"
                              data-testid="product-image-input"
                            />
                          </div>
                        </div>

                        {/* Gallery Images Upload */}
                        <div className="space-y-2">
                          <Label className="text-gray-700 font-medium text-sm flex items-center">
                            <Upload className="w-4 h-4 mr-2 text-purple-500" />
                            Gallery Images (optional)
                          </Label>
                          <ImageUpload
                            onUploadComplete={(urls) => {
                              const currentImages = productForm.images ? productForm.images.split(',').map(u => u.trim()).filter(u => u) : [];
                              const newImages = Array.isArray(urls) ? urls : [urls];
                              const allImages = [...currentImages, ...newImages];
                              setProductForm({ ...productForm, images: allImages.join(', ') });
                            }}
                            multiple={true}
                            folder="alaxico/products"
                            existingImages={productForm.images ? productForm.images.split(',').map(u => u.trim()).filter(u => u) : []}
                            onRemoveImage={(index) => {
                              const images = productForm.images.split(',').map(u => u.trim()).filter(u => u);
                              images.splice(index, 1);
                              setProductForm({ ...productForm, images: images.join(', ') });
                            }}
                            maxFiles={5}
                          />
                          <p className="text-xs text-gray-400">Upload up to 5 gallery images for the product carousel</p>
                        </div>

                        <div>
                          <Label className="text-gray-700 font-medium text-sm">Key Features (comma-separated)</Label>
                          <Input
                            value={productForm.keyFeatures}
                            onChange={(e) => setProductForm({ ...productForm, keyFeatures: e.target.value })}
                            placeholder="Premium Quality, ISO Certified, Easy to Use, 1 Year Warranty"
                            className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                          />
                          <p className="text-xs text-gray-400 mt-1">These appear as checkmarks on the product page</p>
                        </div>
                      </div>

                      {/* Feature Highlights Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <Star className="w-4 h-4 mr-2 text-purple-500" />
                          Feature Highlights (shown in product page)
                        </h3>
                        <p className="text-xs text-gray-500">Add up to 4 feature highlights with icons</p>
                        {productForm.featureHighlights.map((fh, idx) => (
                          <div key={`highlight-${idx}-${fh.icon}-${fh.text?.slice(0,10)}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-3 bg-purple-50 rounded-lg">
                            <div className="sm:col-span-3">
                              <Label className="text-xs text-gray-600">Icon</Label>
                              <Select 
                                value={fh.icon} 
                                onValueChange={(v) => {
                                  const newHighlights = [...productForm.featureHighlights];
                                  newHighlights[idx].icon = v;
                                  setProductForm({ ...productForm, featureHighlights: newHighlights });
                                }}
                              >
                                <SelectTrigger className="mt-1 border-gray-200 text-sm">
                                  <SelectValue placeholder="Icon" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="zap">Zap (Fast)</SelectItem>
                                  <SelectItem value="shield">Shield (Safe)</SelectItem>
                                  <SelectItem value="timer">Timer (Auto)</SelectItem>
                                  <SelectItem value="award">Award (Quality)</SelectItem>
                                  <SelectItem value="thermometer">Thermometer</SelectItem>
                                  <SelectItem value="volume">Volume (Sound)</SelectItem>
                                  <SelectItem value="check">Check (Certified)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="sm:col-span-4">
                              <Label className="text-xs text-gray-600">Title</Label>
                              <Input
                                value={fh.title}
                                onChange={(e) => {
                                  const newHighlights = [...productForm.featureHighlights];
                                  newHighlights[idx].title = e.target.value;
                                  setProductForm({ ...productForm, featureHighlights: newHighlights });
                                }}
                                placeholder="Feature title"
                                className="mt-1 border-gray-200 text-sm"
                              />
                            </div>
                            <div className="sm:col-span-5">
                              <Label className="text-xs text-gray-600">Description</Label>
                              <Input
                                value={fh.description}
                                onChange={(e) => {
                                  const newHighlights = [...productForm.featureHighlights];
                                  newHighlights[idx].description = e.target.value;
                                  setProductForm({ ...productForm, featureHighlights: newHighlights });
                                }}
                                placeholder="Short description"
                                className="mt-1 border-gray-200 text-sm"
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pricing Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <Percent className="w-4 h-4 mr-2 text-purple-500" />
                          Pricing & Discount
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                          💡 Selling Price must be less than Original Price (MRP). Discount will auto-calculate.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Selling Price (₹) *</Label>
                            <p className="text-xs text-green-600 mb-1">Customer pays this amount</p>
                            <Input
                              type="number"
                              value={productForm.price}
                              onChange={(e) => {
                                const sellingPrice = parseFloat(e.target.value) || 0;
                                const originalPrice = parseFloat(productForm.originalPrice) || 0;
                                let discount = '';
                                let priceError = '';
                                
                                if (originalPrice > 0 && sellingPrice > 0) {
                                  if (sellingPrice >= originalPrice) {
                                    priceError = 'Selling price must be less than MRP';
                                  } else {
                                    discount = Math.round(((originalPrice - sellingPrice) / originalPrice) * 100).toString();
                                  }
                                }
                                setProductForm({ ...productForm, price: e.target.value, discount, priceError });
                              }}
                              required
                              placeholder="1499"
                              className={`mt-1 text-sm ${
                                productForm.priceError 
                                  ? 'border-red-500 focus:border-red-500' 
                                  : 'border-gray-200 focus:border-purple-500'
                              }`}
                              data-testid="product-price-input"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Original Price / MRP (₹) *</Label>
                            <p className="text-xs text-gray-500 mb-1">Shown crossed out (must be higher)</p>
                            <Input
                              type="number"
                              value={productForm.originalPrice}
                              onChange={(e) => {
                                const originalPrice = parseFloat(e.target.value) || 0;
                                const sellingPrice = parseFloat(productForm.price) || 0;
                                let discount = '';
                                let priceError = '';
                                
                                if (originalPrice > 0 && sellingPrice > 0) {
                                  if (sellingPrice >= originalPrice) {
                                    priceError = 'MRP must be greater than selling price';
                                  } else {
                                    discount = Math.round(((originalPrice - sellingPrice) / originalPrice) * 100).toString();
                                  }
                                }
                                setProductForm({ ...productForm, originalPrice: e.target.value, discount, priceError });
                              }}
                              required
                              placeholder="2499"
                              className={`mt-1 text-sm ${
                                productForm.priceError 
                                  ? 'border-red-500 focus:border-red-500' 
                                  : 'border-gray-200 focus:border-purple-500'
                              }`}
                              data-testid="product-original-price-input"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Discount (%)</Label>
                            <p className="text-xs text-purple-600 mb-1">Auto-calculated</p>
                            <Input
                              type="number"
                              value={productForm.discount}
                              readOnly
                              placeholder="Auto"
                              max="100"
                              className="mt-1 border-gray-200 bg-gray-50 text-sm cursor-not-allowed"
                              data-testid="product-discount-input"
                            />
                          </div>
                        </div>
                        
                        {/* Price Error Message */}
                        {productForm.priceError && (
                          <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-sm font-medium text-red-600 flex items-center">
                              ❌ {productForm.priceError}
                            </p>
                            <p className="text-xs text-red-500 mt-1">
                              Example: If MRP is ₹2,499 then Selling Price should be less (e.g., ₹1,499)
                            </p>
                          </div>
                        )}
                        
                        {/* Price Preview - Only show when valid */}
                        {productForm.price && productForm.originalPrice && !productForm.priceError && parseFloat(productForm.price) < parseFloat(productForm.originalPrice) && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-sm font-medium text-green-800">
                              ✅ Preview: 
                              <span className="line-through text-gray-400 ml-2">₹{productForm.originalPrice}</span>
                              <span className="text-green-600 ml-2 text-lg font-bold">₹{productForm.price}</span>
                              {productForm.discount && (
                                <span className="ml-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded">{productForm.discount}% OFF</span>
                              )}
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              Customer saves ₹{(parseFloat(productForm.originalPrice) - parseFloat(productForm.price)).toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Stock Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <Box className="w-4 h-4 mr-2 text-purple-500" />
                          Inventory & Stock
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Stock Quantity</Label>
                            <Input
                              type="number"
                              value={productForm.stockQuantity}
                              onChange={(e) => setProductForm({ ...productForm, stockQuantity: e.target.value })}
                              placeholder="100"
                              className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                              data-testid="product-stock-input"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-medium text-sm">Min Order Quantity</Label>
                            <Input
                              type="number"
                              value={productForm.minOrderQuantity}
                              onChange={(e) => setProductForm({ ...productForm, minOrderQuantity: e.target.value })}
                              placeholder="1"
                              min="1"
                              className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                              data-testid="product-min-order-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Warranty & Shipping Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <ShieldCheck className="w-4 h-4 mr-2 text-purple-500" />
                          Warranty & Shipping
                        </h3>
                        <div>
                          <Label className="text-gray-700 font-medium text-sm">Warranty Information</Label>
                          <Textarea
                            value={productForm.warrantyInfo}
                            onChange={(e) => setProductForm({ ...productForm, warrantyInfo: e.target.value })}
                            placeholder="1 Year Manufacturer Warranty covering manufacturing defects"
                            rows={2}
                            className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700 font-medium text-sm">Shipping Information</Label>
                          <Textarea
                            value={productForm.shippingInfo}
                            onChange={(e) => setProductForm({ ...productForm, shippingInfo: e.target.value })}
                            placeholder="Free shipping on all orders across India. Delivery in 2-4 days."
                            rows={2}
                            className="mt-1 border-gray-200 focus:border-purple-500 text-sm"
                          />
                        </div>
                      </div>

                      {/* Payment Methods Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                          <Tag className="w-4 h-4 mr-2 text-purple-500" />
                          Allowed Payment Methods
                        </h3>
                        <p className="text-xs text-gray-500">Select which payment methods are available for this product</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                          {PAYMENT_METHODS.map((method) => (
                            <label 
                              key={method.id}
                              htmlFor={`payment-method-${method.id}`}
                              className={`flex items-center justify-between p-2 sm:p-3 rounded-lg cursor-pointer border-2 transition-all ${
                                productForm.paymentMethods?.includes(method.id) 
                                  ? 'border-purple-500 bg-purple-50' 
                                  : 'border-gray-200 bg-white hover:border-purple-200'
                              }`}
                            >
                              <div>
                                <p className="font-medium text-gray-900 text-xs sm:text-sm">{method.name}</p>
                                <p className="text-[10px] sm:text-xs text-gray-500">{method.description}</p>
                              </div>
                              <Switch
                                id={`payment-method-${method.id}`}
                                checked={productForm.paymentMethods?.includes(method.id)}
                                onCheckedChange={() => togglePaymentMethod(method.id)}
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Options Section */}
                      <div className="space-y-3 sm:space-y-4 border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700">Product Options</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                          <div className="flex items-center justify-between p-2 sm:p-3 bg-purple-50 rounded-lg">
                            <Label htmlFor="availability-switch" className="text-gray-700 font-medium cursor-pointer text-xs sm:text-sm">Available</Label>
                            <Switch
                              id="availability-switch"
                              checked={productForm.availability}
                              onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, availability: checked }))}
                              data-testid="product-availability-switch"
                            />
                          </div>
                          <div className="flex items-center justify-between p-2 sm:p-3 bg-purple-50 rounded-lg">
                            <Label htmlFor="featured-switch" className="text-gray-700 font-medium cursor-pointer text-xs sm:text-sm">Featured</Label>
                            <Switch
                              id="featured-switch"
                              checked={productForm.featured}
                              onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, featured: checked }))}
                              data-testid="product-featured-switch"
                            />
                          </div>
                          <div className="flex items-center justify-between p-2 sm:p-3 bg-purple-50 rounded-lg">
                            <Label htmlFor="limited-switch" className="text-gray-700 font-medium cursor-pointer text-xs sm:text-sm">Limited Stock</Label>
                            <Switch
                              id="limited-switch"
                              checked={productForm.limitedStock}
                              onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, limitedStock: checked }))}
                              data-testid="product-limited-switch"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-100">
                        <Button 
                          type="submit" 
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white shadow-md text-sm"
                          data-testid="submit-product-button"
                        >
                          {editingProduct ? (
                            <>
                              <Pencil className="w-4 h-4 mr-2" />
                              Update Product
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Create Product
                            </>
                          )}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleCloseDialog}
                          className="border-gray-200 text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Mobile-friendly Product Cards */}
              <div className="block sm:hidden space-y-3">
                {products.map((product) => (
                  <div key={product.id} className="border border-purple-100 bg-purple-50/50 rounded-xl p-3" data-testid={`admin-product-${product.id}`}>
                    <div className="flex gap-3">
                      {product.image && (
                        <img src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{product.name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{product.category}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-purple-600 font-semibold text-sm">₹{product.price.toLocaleString()}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${product.availability ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.availability ? 'In Stock' : 'Out'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-purple-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditProduct(product)}
                        className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50 text-xs h-8"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="flex-1 text-xs h-8"
                        data-testid={`delete-product-${product.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-100 text-left">
                      <th className="pb-3 font-semibold text-gray-900">Product</th>
                      <th className="pb-3 font-semibold text-gray-900">Category</th>
                      <th className="pb-3 font-semibold text-gray-900">Price</th>
                      <th className="pb-3 font-semibold text-gray-900">Status</th>
                      <th className="pb-3 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-b border-purple-50" data-testid={`admin-product-${product.id}`}>
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            {product.image && (
                              <img src={product.image} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                            )}
                            <span className="text-gray-700 font-medium">{product.name}</span>
                          </div>
                        </td>
                        <td className="py-3 text-gray-500">{product.category}</td>
                        <td className="py-3 text-purple-600 font-medium">₹{product.price.toLocaleString()}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded text-xs ${product.availability ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.availability ? 'Available' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                              className="border-purple-200 text-purple-600 hover:bg-purple-50"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              data-testid={`delete-product-${product.id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Orders Management</h2>
              <div className="space-y-4 sm:space-y-6">
                {orders.map((order) => (
                  <div key={order.id} className="border-2 border-purple-200 bg-white rounded-xl overflow-hidden shadow-sm" data-testid={`admin-order-${order.id}`}>
                    {/* Order Header */}
                    <div className="bg-purple-50 p-3 sm:p-4 border-b border-purple-200">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div>
                          <p className="font-bold text-gray-900 text-base sm:text-lg">Order #{order.id.slice(0, 8)}</p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString('en-IN', { 
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                            order.payment_status === 'completed' ? 'bg-green-500 text-white' : 
                            order.payment_status === 'pay_on_delivery' ? 'bg-orange-500 text-white' :
                            'bg-yellow-500 text-white'
                          }`}>
                            💳 {order.payment_status === 'completed' ? 'PAID' : 
                               order.payment_status === 'pay_on_delivery' ? 'COD' : 
                               order.payment_status?.toUpperCase() || 'PENDING'}
                          </span>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                            order.order_status === 'delivered' ? 'bg-green-500 text-white' :
                            order.order_status === 'shipped' ? 'bg-blue-500 text-white' :
                            order.order_status === 'packed' ? 'bg-indigo-500 text-white' :
                            order.order_status === 'processing' ? 'bg-purple-500 text-white' :
                            order.order_status === 'cancelled' ? 'bg-red-500 text-white' :
                            'bg-yellow-500 text-white'
                          }`}>
                            📦 {order.order_status?.toUpperCase() || 'PENDING'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 sm:p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Left: Delivery Address - PROMINENT */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                          <h4 className="font-bold text-blue-800 text-sm mb-3 flex items-center">
                            🚚 DELIVER TO:
                          </h4>
                          {order.delivery_address ? (
                            <div className="space-y-2">
                              <p className="font-bold text-gray-900 text-base">
                                {order.delivery_address.name}
                              </p>
                              <p className="text-gray-700 flex items-center gap-2">
                                <span className="text-lg">📞</span>
                                <span className="font-semibold">{order.delivery_address.phone}</span>
                              </p>
                              <div className="text-gray-600 text-sm leading-relaxed">
                                <p>{order.delivery_address.address || order.delivery_address.street}</p>
                                <p className="font-medium">
                                  {order.delivery_address.city}, {order.delivery_address.state}
                                </p>
                                <p className="font-bold text-blue-700 text-base">
                                  PIN: {order.delivery_address.pincode}
                                </p>
                              </div>
                              {order.delivery_address.landmark && (
                                <p className="text-gray-500 text-xs">
                                  Landmark: {order.delivery_address.landmark}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-red-500">No address provided</p>
                          )}
                          
                          {/* Customer Info */}
                          {order.user && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <p className="text-xs text-blue-600">
                                <span className="font-medium">Customer:</span> {order.user.email}
                              </p>
                              {order.user.phone && (
                                <p className="text-xs text-blue-600">
                                  <span className="font-medium">Alt Phone:</span> {order.user.phone}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Right: Order Items */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <h4 className="font-bold text-gray-700 text-sm mb-3">📦 ORDER ITEMS:</h4>
                          <div className="space-y-2">
                            {order.items?.map((item) => (
                              <div key={`${order.id}-${item.product_id || item.product_name}`} className="flex justify-between py-2 border-b border-gray-200 last:border-0">
                                <div>
                                  <p className="text-gray-900 font-medium text-sm">{item.product_name}</p>
                                  <p className="text-gray-500 text-xs">Qty: {item.quantity}</p>
                                </div>
                                <span className="font-semibold text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between pt-3 mt-2 border-t-2 border-gray-300">
                            <span className="font-bold text-gray-900">TOTAL AMOUNT</span>
                            <span className="font-bold text-purple-600 text-lg">₹{order.total_amount?.toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Payment: {order.payment_method === 'cod' ? 'Cash on Delivery' : 
                                     order.payment_method === 'razorpay' ? 'Online Payment' :
                                     order.payment_method === 'upi' ? 'UPI' :
                                     order.payment_method === 'bank_transfer' ? 'Bank Transfer' :
                                     order.payment_method || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Order Status Update */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-bold text-gray-700 mb-3">⚡ UPDATE ORDER STATUS:</p>
                        <div className="flex flex-wrap gap-2">
                          {['pending', 'processing', 'packed', 'shipped', 'delivered', 'cancelled'].map((status) => (
                            <Button
                              key={status}
                              size="sm"
                              variant={order.order_status === status ? 'default' : 'outline'}
                              className={`text-xs h-8 ${
                                order.order_status === status 
                                  ? 'bg-purple-600 text-white' 
                                  : 'border-purple-200 text-purple-600 hover:bg-purple-50'
                              }`}
                              onClick={() => handleOrderStatusUpdate(order.id, status)}
                            >
                              {status === 'pending' && '⏳'}
                              {status === 'processing' && '⚙️'}
                              {status === 'packed' && '📦'}
                              {status === 'shipped' && '🚚'}
                              {status === 'delivered' && '✅'}
                              {status === 'cancelled' && '❌'}
                              {' '}{status.charAt(0).toUpperCase() + status.slice(1)}
                            </Button>
                          ))}
                        </div>
                      
                        {/* Payment Confirmation (for COD/Bank Transfer) */}
                        {(order.payment_status === 'pay_on_delivery' || order.payment_status === 'awaiting_confirmation') && (
                          <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-xs font-semibold text-yellow-800 mb-2">
                              💳 Payment Status: {order.payment_status === 'pay_on_delivery' ? 'Cash on Delivery' : 'Awaiting Bank Transfer Confirmation'}
                            </p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="text-xs h-7 bg-green-600 hover:bg-green-700"
                                onClick={() => handlePaymentConfirmation(order.id, 'completed')}
                              >
                                ✓ Confirm Payment Received
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handlePaymentConfirmation(order.id, 'failed')}
                              >
                                ✗ Payment Failed
                              </Button>
                            </div>
                          </div>
                        )}
                      
                        {/* Tracking Info Input (show when shipped) */}
                        {(order.order_status === 'shipped' || order.order_status === 'packed') && (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-purple-100">
                            <p className="text-xs font-semibold text-gray-700 mb-2">🚚 Add Tracking Info:</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <Input
                                placeholder="Courier Name"
                                className="text-xs h-8"
                                id={`courier-${order.id}`}
                                defaultValue={order.courier_name || ''}
                              />
                              <Input
                                placeholder="Tracking Number"
                                className="text-xs h-8"
                                id={`tracking-${order.id}`}
                                defaultValue={order.tracking_number || ''}
                              />
                              <Input
                                placeholder="Est. Delivery (e.g., Apr 5)"
                                className="text-xs h-8"
                                id={`delivery-${order.id}`}
                                defaultValue={order.estimated_delivery || ''}
                              />
                            </div>
                            <Button
                              size="sm"
                              className="mt-2 text-xs h-7 bg-purple-600"
                              onClick={() => {
                                const courier = document.getElementById(`courier-${order.id}`).value;
                                const tracking = document.getElementById(`tracking-${order.id}`).value;
                                const delivery = document.getElementById(`delivery-${order.id}`).value;
                                handleOrderStatusUpdate(order.id, order.order_status, courier, tracking, delivery);
                              }}
                            >
                              Save Tracking Info
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {orders.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No orders yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bulk Enquiries Tab */}
          <TabsContent value="enquiries">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Bulk Enquiries Management</h2>
              <div className="space-y-3 sm:space-y-4">
                {bulkEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="border border-purple-100 bg-purple-50 rounded-lg p-3 sm:p-4" data-testid={`admin-enquiry-${enquiry.id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2 sm:mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{enquiry.product?.name}</p>
                        <p className="text-xs sm:text-sm text-gray-500">{enquiry.organization_name} - {enquiry.buyer_type}</p>
                        <p className="text-xs sm:text-sm text-gray-500">Quantity: {enquiry.quantity}</p>
                      </div>
                      <Select value={enquiry.status} onValueChange={(v) => handleEnquiryStatus(enquiry.id, v)}>
                        <SelectTrigger className="w-full sm:w-32 text-xs sm:text-sm h-8 sm:h-9" data-testid={`enquiry-status-${enquiry.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {bulkEnquiries.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No bulk enquiries yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* B2B Enquiries Tab */}
          <TabsContent value="b2b">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">B2B Business Enquiries</h2>
              <div className="space-y-3 sm:space-y-4">
                {b2bEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="border border-purple-100 bg-purple-50 rounded-lg p-3 sm:p-4" data-testid={`admin-b2b-enquiry-${enquiry.id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2 sm:mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-purple-200 text-purple-800 rounded-full text-xs font-medium">
                            {enquiry.business_type}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(enquiry.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{enquiry.business_name}</p>
                        <p className="text-xs sm:text-sm text-gray-600">{enquiry.contact_person}</p>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                          <span>📧 {enquiry.email}</span>
                          <span>📞 {enquiry.phone}</span>
                        </div>
                        {enquiry.estimated_quantity && (
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">Est. Quantity: {enquiry.estimated_quantity}</p>
                        )}
                        {enquiry.products_interested && (
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">Products: {enquiry.products_interested}</p>
                        )}
                        {enquiry.message && (
                          <p className="text-xs sm:text-sm text-gray-600 mt-2 bg-white p-2 rounded border">{enquiry.message}</p>
                        )}
                      </div>
                      <Select value={enquiry.status} onValueChange={(v) => handleB2bEnquiryStatus(enquiry.id, v)}>
                        <SelectTrigger className="w-full sm:w-32 text-xs sm:text-sm h-8 sm:h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {b2bEnquiries.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No B2B enquiries yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Partner Applications Tab */}
          <TabsContent value="partners">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Partner Applications</h2>
              <div className="space-y-3 sm:space-y-4">
                {partnerApplications.map((application) => (
                  <div key={application.id} className="border border-purple-100 bg-purple-50 rounded-lg p-3 sm:p-4" data-testid={`admin-partner-${application.id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2 sm:mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            {application.program_type}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(application.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{application.name}</p>
                        {application.organization && (
                          <p className="text-xs sm:text-sm text-gray-600">{application.organization}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                          <span>📧 {application.email}</span>
                          <span>📞 {application.phone}</span>
                          <span>📍 {application.city}</span>
                        </div>
                        {application.message && (
                          <p className="text-xs sm:text-sm text-gray-600 mt-2 bg-white p-2 rounded border">{application.message}</p>
                        )}
                      </div>
                      <Select value={application.status} onValueChange={(v) => handlePartnerApplicationStatus(application.id, v)}>
                        <SelectTrigger className="w-full sm:w-32 text-xs sm:text-sm h-8 sm:h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {partnerApplications.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No partner applications yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Reviews Management</h2>
              <div className="space-y-3 sm:space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border border-purple-100 bg-purple-50 rounded-xl p-3 sm:p-5" data-testid={`admin-review-${review.id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3 sm:mb-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-200 rounded-full flex items-center justify-center text-purple-700 font-semibold text-base sm:text-lg flex-shrink-0">
                          {review.user?.name?.charAt(0) || 'U'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base">{review.user?.name}</p>
                          <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 flex-wrap">
                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate max-w-[180px] sm:max-w-none">{review.user?.email}</span>
                          </div>
                          <div className="flex items-center mt-1">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-3 h-3 sm:w-4 sm:h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2 self-start">
                        {!review.approved ? (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-xs h-8"
                              onClick={() => handleApproveReview(review.id, true)}
                              data-testid={`approve-review-${review.id}`}
                            >
                              ✓ Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="text-xs h-8"
                              onClick={() => handleApproveReview(review.id, false)}
                              data-testid={`reject-review-${review.id}`}
                            >
                              ✕ Reject
                            </Button>
                          </>
                        ) : (
                          <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">✓ Approved</span>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs sm:text-sm text-purple-600 font-medium mb-2">Product: {review.product?.name}</p>
                      <p className="text-gray-700 bg-white p-2 sm:p-3 rounded-lg border border-gray-100 text-sm">{review.comment}</p>
                      
                      {/* Review Images */}
                      {review.images && review.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="text-xs text-gray-500 w-full">Attached Photos:</span>
                          {review.images.map((img, idx) => (
                            <a key={`${review.id}-img-${img.substring(img.length - 15)}`} href={img} target="_blank" rel="noopener noreferrer" className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-purple-400">
                              <img src={img} alt={`Review photo ${idx + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      {/* Review Video */}
                      {review.video_url && (
                        <div className="mt-3">
                          <span className="text-xs text-gray-500">Attached Video: </span>
                          <a href={review.video_url} target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-purple-600 hover:underline break-all">
                            {review.video_url}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No reviews to manage</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Verifications Tab */}
          <TabsContent value="verifications">
            <div className="bg-white border border-purple-100 rounded-xl p-3 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Verification Requests</h2>
              <div className="space-y-3 sm:space-y-4">
                {verifications.map((verification) => (
                  <div key={verification.id} className="border border-purple-100 bg-purple-50 rounded-lg p-3 sm:p-4" data-testid={`admin-verification-${verification.id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2 sm:mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{verification.user?.name}</p>
                        <p className="text-xs sm:text-sm text-gray-500">{verification.organization_name} - {verification.buyer_type}</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">Document: {verification.documents?.info}</p>
                      </div>
                      {verification.status === 'pending' && (
                        <div className="flex space-x-2 self-start">
                          <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-xs h-8"
                            onClick={() => handleVerificationAction(verification.id, 'approved')}
                            data-testid={`approve-verification-${verification.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs h-8"
                            onClick={() => handleVerificationAction(verification.id, 'rejected')}
                            data-testid={`reject-verification-${verification.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                      {verification.status !== 'pending' && (
                        <span className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm self-start ${verification.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {verification.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {verifications.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No verification requests</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
